/**
 * Product Research Redux Slice
 * Manages UI state for product research functionality
 * Analysis logic is handled by searchNicheSlice
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import { ResearchHistoryApiService } from '../api/researchHistoryApi';

export interface ProductResearchState {
    // UI state
    isDetailsOpen: boolean;
    isFirstVisit: boolean;
    selectedNicheId: string;
    // Favourite niche IDs map (niche_id → true)
    favouriteNicheIds: Record<string, boolean>;
}

const initialState: ProductResearchState = {
    isDetailsOpen: true,
    isFirstVisit: true,
    selectedNicheId: '',
    favouriteNicheIds: {},
};

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Fetch only favourited niches (lightweight: only_favourites=true)
 * and build an ID-based lookup map.
 */
export const fetchFavouriteNicheIds = createAsyncThunk<
    Record<string, boolean>,
    void,
    { rejectValue: string }
>(
    'productResearch/fetchFavouriteNicheIds',
    async (_, { rejectWithValue }) => {
        try {
            const response = await ResearchHistoryApiService.fetchHistory({
                only_favourites: true,
                limit: 100,
            });
            const map: Record<string, boolean> = {};
            for (const item of response.items) {
                for (const niche of item.niches) {
                    if (niche.is_favourite) {
                        map[String(niche.id)] = true;
                    }
                }
            }
            return map;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch favourite niches';
            return rejectWithValue(message);
        }
    }
);

// Product Research Slice
const productResearchSlice = createSlice({
    name: 'productResearch',
    initialState,
    reducers: {
        // Toggle Details panel
        toggleDetails: (state) => {
            state.isDetailsOpen = !state.isDetailsOpen;
        },
        
        // Set Details panel state
        setDetailsOpen: (state, action: PayloadAction<boolean>) => {
            state.isDetailsOpen = action.payload;
        },
        
        // Set first visit flag
        setFirstVisit: (state, action: PayloadAction<boolean>) => {
            state.isFirstVisit = action.payload;
        },

        // Set selected niche id (shared between Details and Chat)
        setSelectedNicheId: (state, action: PayloadAction<string>) => {
            state.selectedNicheId = action.payload;
        },

        // Optimistic favourite toggle for a niche
        setNicheFavourite: (state, action: PayloadAction<{ nicheId: string; isFavourite: boolean }>) => {
            if (action.payload.isFavourite) {
                state.favouriteNicheIds[action.payload.nicheId] = true;
            } else {
                delete state.favouriteNicheIds[action.payload.nicheId];
            }
        },
        
        // Reset to initial state
        resetProductResearch: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchFavouriteNicheIds.fulfilled, (state, action) => {
            state.favouriteNicheIds = action.payload;
        });
    },
});

// Export actions
export const {
    toggleDetails,
    setDetailsOpen,
    setFirstVisit,
    setSelectedNicheId,
    setNicheFavourite,
    resetProductResearch,
} = productResearchSlice.actions;

// Selectors
export const selectIsDetailsOpen = (state: RootState) => state.productResearch?.isDetailsOpen || false;
export const selectIsFirstVisit = (state: RootState) => state.productResearch?.isFirstVisit ?? true;
export const selectSelectedNicheId = (state: RootState) => state.productResearch?.selectedNicheId ?? '';
export const selectFavouriteNicheIds = (state: RootState) => state.productResearch?.favouriteNicheIds ?? {};
export const selectIsNicheFavourite = (state: RootState, nicheId: string) =>
    Boolean(state.productResearch?.favouriteNicheIds?.[nicheId]);

// Export reducer
export default productResearchSlice.reducer;
